import axiosInstance from './api/axiosInstance';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error' | 'announcement';
  targetAudience: 'all' | 'users' | 'vendors' | 'product_vendors' | 'service_vendors' | 'general_vendors';
  recipients?: string[];
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  sentAt: string;
  emailSent: boolean;
  emailSentAt?: string;
  isRead: boolean;
  readBy?: Array<{
    user: string;
    readAt: string;
  }>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNotificationData {
  title: string;
  message: string;
  type?: 'info' | 'warning' | 'success' | 'error' | 'announcement';
  targetAudience: 'all' | 'users' | 'vendors' | 'product_vendors' | 'service_vendors' | 'general_vendors';
  recipientIds?: string[];
}

export interface NotificationFilters {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

class NotificationService {
  // Get user's notifications
  async getNotifications(filters: NotificationFilters = {}): Promise<{
    success: boolean;
    data: {
      notifications: Notification[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
      };
    };
  }> {
    const response = await axiosInstance.get('/notifications', { params: filters });
    return response.data;
  }

  // Get notification by ID
  async getNotificationById(notificationId: string): Promise<{
    success: boolean;
    data: { notification: Notification };
  }> {
    const response = await axiosInstance.get(`/notifications/${notificationId}`);
    return response.data;
  }

  // Get unread count
  async getUnreadCount(): Promise<{
    success: boolean;
    data: { unreadCount: number };
  }> {
    const response = await axiosInstance.get('/notifications/unread-count');
    return response.data;
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await axiosInstance.patch(`/notifications/${notificationId}/read`);
    return response.data;
  }

  // Mark all notifications as read
  async markAllAsRead(): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await axiosInstance.patch('/notifications/read-all');
    return response.data;
  }

  // Create notification (admin only)
  async createNotification(notificationData: CreateNotificationData): Promise<{
    success: boolean;
    data: { notification: Notification };
    message: string;
  }> {
    const response = await axiosInstance.post('/notifications', notificationData);
    return response.data;
  }

  // Get all notifications (admin only)
  async getAllNotifications(filters: NotificationFilters = {}): Promise<{
    success: boolean;
    data: {
      notifications: Notification[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
      };
    };
  }> {
    const response = await axiosInstance.get('/notifications/admin/all', { params: filters });
    return response.data;
  }

  // Delete notification (admin only)
  async deleteNotification(notificationId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await axiosInstance.delete(`/notifications/${notificationId}`);
    return response.data;
  }
}

export const notificationService = new NotificationService();






